/**
 * Time-zone helpers shared by every elder-profile form (Welcome,
 * SetupDevice, Settings → Profile). Single source of truth for the
 * shortlist of zones and for the browser-detect default so an
 * international caregiver isn't silently dropped into Europe/London.
 */

import { fromZonedTime, formatInTimeZone } from 'date-fns-tz';
import { addDays } from '@/lib/dateUtil';

export interface TimeZoneOption {
  value: string;
  label: string;
}

/** Shortlist shown in the dropdown — the major English-speaking markets
 * (UK/Ireland, US, Canada, Australia, New Zealand, South Africa) plus a
 * couple of nearby European zones for expats. The caregiver's detected
 * zone is always prepended on top of this (see {@link timeZoneOptions}),
 * so any IANA zone — including ones not listed here, like India or
 * Newfoundland — is still selectable. */
export const COMMON_TIMEZONES: TimeZoneOption[] = [
  { value: 'Europe/London', label: 'United Kingdom (London)' },
  { value: 'Europe/Dublin', label: 'Ireland (Dublin)' },
  { value: 'America/New_York', label: 'United States — Eastern (New York)' },
  { value: 'America/Chicago', label: 'United States — Central (Chicago)' },
  { value: 'America/Denver', label: 'United States — Mountain (Denver)' },
  { value: 'America/Los_Angeles', label: 'United States — Pacific (Los Angeles)' },
  { value: 'America/Toronto', label: 'Canada — Eastern (Toronto)' },
  { value: 'America/Winnipeg', label: 'Canada — Central (Winnipeg)' },
  { value: 'America/Edmonton', label: 'Canada — Mountain (Edmonton)' },
  { value: 'America/Vancouver', label: 'Canada — Pacific (Vancouver)' },
  { value: 'America/Halifax', label: 'Canada — Atlantic (Halifax)' },
  { value: 'Australia/Perth', label: 'Australia — Western (Perth)' },
  { value: 'Australia/Sydney', label: 'Australia — Eastern (Sydney)' },
  { value: 'Pacific/Auckland', label: 'New Zealand (Auckland)' },
  { value: 'Africa/Johannesburg', label: 'South Africa (Johannesburg)' },
  { value: 'Europe/Paris', label: 'France (Paris)' },
  { value: 'Europe/Berlin', label: 'Germany (Berlin)' },
];

const FALLBACK_TIMEZONE = 'Europe/London';

/** The browser's IANA zone, or Europe/London if the runtime won't tell
 * us (very old browsers, locked-down environments). */
export function detectTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || FALLBACK_TIMEZONE;
  } catch {
    return FALLBACK_TIMEZONE;
  }
}

/** The dropdown options for a `<select>` whose current value is
 * `current`. Ensures `current` is always present (so a detected zone
 * like America/St_Johns that isn't in the shortlist still shows as
 * selected rather than silently falling back to the first option). The
 * prepended zone is labelled with its raw IANA id, since we have no
 * friendly name for the long tail. */
export function timeZoneOptions(current: string): TimeZoneOption[] {
  if (!current || COMMON_TIMEZONES.some((tz) => tz.value === current)) {
    return COMMON_TIMEZONES;
  }
  return [{ value: current, label: current }, ...COMMON_TIMEZONES];
}

/** Resolve an IANA zone, degrading to the fallback for an invalid one.
 * `accounts/{aid}.timeZone` is only length-bounded by firestore.rules, so a
 * stale/hand-edited value (e.g. "Mars/Phobos") can reach the app; a bad zone
 * must never blank a page or throw a RangeError mid-render. */
export function safeTimeZone(tz: string | undefined | null): string {
  if (!tz) return FALLBACK_TIMEZONE;
  try {
    // Throws RangeError for a non-IANA zone.
    new Intl.DateTimeFormat('en-GB', { timeZone: tz });
    return tz;
  } catch {
    return FALLBACK_TIMEZONE;
  }
}

// --- Wall-clock ⇄ UTC-instant conversions in a given zone -----------------
//
// Calendar event times are stored as UTC instants but entered, displayed, and
// edited as wall-clock in the ELDER's account tz — NOT the caregiver's browser
// zone. These wrap date-fns-tz so every caller shares one tz-safe conversion
// (see docs/dev-ui-sweep-2026-07-10.md / #141).

/** A wall-clock `YYYY-MM-DD` + `HH:MM` interpreted in `tz` → the UTC instant. */
export function wallClockToInstant(dateStr: string, timeStr: string, tz: string): Date {
  return fromZonedTime(`${dateStr}T${timeStr}:00`, safeTimeZone(tz));
}

/** A UTC instant → its `YYYY-MM-DD` calendar date in `tz`. */
export function instantToDateStr(instant: Date, tz: string): string {
  return formatInTimeZone(instant, safeTimeZone(tz), 'yyyy-MM-dd');
}

/** A UTC instant → its `HH:MM` wall-clock time in `tz`. */
export function instantToTimeStr(instant: Date, tz: string): string {
  return formatInTimeZone(instant, safeTimeZone(tz), 'HH:mm');
}

/** A UTC instant → a *floating* (offset-less) ISO of its wall-clock in `tz`,
 * e.g. "2026-07-10T09:00:00". Fed to FullCalendar (local mode) and the rrule
 * plugin so an event renders at its account-tz wall-clock regardless of the
 * caregiver's browser zone. */
export function instantToFloatingIso(instant: Date, tz: string): string {
  return formatInTimeZone(instant, safeTimeZone(tz), "yyyy-MM-dd'T'HH:mm:ss");
}

/** Today's `YYYY-MM-DD` in `tz` — for new-event date defaults so "+ Add event"
 * seeds the elder's today, not the caregiver's. `now` is injectable for tests. */
export function todayInTz(tz: string, now: Date = new Date()): string {
  return formatInTimeZone(now, safeTimeZone(tz), 'yyyy-MM-dd');
}

/** A FullCalendar (local/floating mode) Date → the UTC instant of its
 * wall-clock re-anchored to `tz`. FC hands back a Date whose *browser-local*
 * components equal the account-tz label the user clicked/dragged onto the
 * grid; re-interpret that label in the account tz. (Storing the Date's raw
 * instant instead would persist the browser reading — the #141 bug.) */
export function fcLocalDateToInstant(d: Date, tz: string): Date {
  const p = (n: number) => String(n).padStart(2, '0');
  const dateStr = `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
  const timeStr = `${p(d.getHours())}:${p(d.getMinutes())}`;
  return wallClockToInstant(dateStr, timeStr, tz);
}

/** Re-anchor a recurring series' start/end instant when the user drags ONE
 * occurrence to a new day/time and picks "all occurrences". FullCalendar hands
 * back the dragged occurrence's old/new positions as local-mode Dates whose
 * browser-local components are the account-tz wall-clock label. We shift the
 * series ANCHOR by the same whole-day offset, set it to the dragged new
 * wall-clock time, and convert through the account tz. DST-correct — unlike
 * applying the absolute ms delta to the anchor, which drifts by an hour when
 * the anchor and the dragged occurrence sit on different-offset dates. */
export function reanchorRecurringInstant(
  anchorInstant: Date, oldFcDate: Date, newFcDate: Date, tz: string,
): Date {
  const p = (n: number) => String(n).padStart(2, '0');
  const isoDate = (d: Date) => `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
  // Whole calendar-day offset the drag applied. Parse date-only ISO as UTC
  // midnights so DST never enters this integer-day difference.
  const dayShift = Math.round((Date.parse(isoDate(newFcDate)) - Date.parse(isoDate(oldFcDate))) / 86_400_000);
  const newTime = `${p(newFcDate.getHours())}:${p(newFcDate.getMinutes())}`;
  return wallClockToInstant(addDays(instantToDateStr(anchorInstant, tz), dayShift), newTime, tz);
}

/** For an all-day event, `endsAt` is the iCal-style *exclusive* next-midnight.
 * Return the inclusive last day (`YYYY-MM-DD`) the form edits, stepping back a
 * calendar day in `tz` via string arithmetic — DST-safe, unlike subtracting
 * 86_400_000 ms from the instant. */
export function inclusiveAllDayEndDate(exclusiveEnd: Date, tz: string): string {
  return addDays(instantToDateStr(exclusiveEnd, tz), -1);
}
